import { Lock, ShieldAlert } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button, Field, Input, Select, Sheet, Textarea } from '@/shared/components/ui'
import { errorMessage } from '@/shared/components/data'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { useCreateAccess } from '../api'
import {
  CATEGORY_KEY,
  CATEGORY_ORDER,
  isCriticalCategory,
  type AccessCategory,
  type AccessKind,
} from '../types'

export interface AccessFormProps {
  companyId: string
  open: boolean
  onClose: () => void
}

/**
 * Alta de un acceso.
 *
 * La regla `prod`/`payments` ⇒ `vaulted` vive en un CHECK de Postgres. Acá se
 * explica ANTES de que alguien la escriba: descubrirla como un error 500 al
 * apretar Guardar, con la contraseña ya tipeada, es la peor forma de enterarse.
 */
export function AccessForm({ companyId, open, onClose }: AccessFormProps) {
  const t = useT()
  const create = useCreateAccess(companyId)

  const [label, setLabel] = useState('')
  const [category, setCategory] = useState<AccessCategory>('panel')
  const [kind, setKind] = useState<AccessKind>('stored')
  const [url, setUrl] = useState('')
  const [username, setUsername] = useState('')
  const [secret, setSecret] = useState('')
  const [vaultProvider, setVaultProvider] = useState('1Password')
  const [vaultItemUrl, setVaultItemUrl] = useState('')
  const [notes, setNotes] = useState('')

  const critical = isCriticalCategory(category)

  // Elegir una categoría crítica mueve el tipo a `vaulted` y tira lo tipeado en
  // el campo del secreto: no queremos ni tenerlo en memoria si no va a viajar.
  useEffect(() => {
    if (critical) {
      setKind('vaulted')
      setSecret('')
    }
  }, [critical])

  if (!open) return null

  const submit = () => {
    create.mutate(
      {
        label: label.trim(),
        category,
        kind,
        url: url.trim() || null,
        username: username.trim() || null,
        ...(kind === 'stored'
          ? { secret }
          : { vault_provider: vaultProvider.trim() || null, vault_item_url: vaultItemUrl.trim() }),
        notes: notes.trim() || null,
      },
      { onSuccess: onClose },
    )
  }

  const valid =
    label.trim().length > 0 &&
    (kind === 'stored' ? secret.length > 0 : vaultItemUrl.trim().length > 0)

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={<h2 className="text-base text-text-1">{t('accesses.new')}</h2>}
      footer={
        <div className="flex items-center justify-between gap-2">
          {create.isError ? (
            <span role="alert" className="text-2xs" style={{ color: 'hsl(var(--overdue))' }}>
              {errorMessage(create.error, t('accesses.form.saveError'))}
            </span>
          ) : (
            <span />
          )}
          <span className="flex gap-2">
            <Button onClick={onClose}>{t('common.action.cancel')}</Button>
            <Button variant="primary" disabled={!valid || create.isPending} onClick={submit}>
              {create.isPending ? t('common.state.saving') : t('common.action.save')}
            </Button>
          </span>
        </div>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (valid) submit()
        }}
      >
        <Field label={t('common.label.name')} htmlFor="access-label">
          <Input
            id="access-label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder={t('accesses.form.namePlaceholder')}
          />
        </Field>

        <Field label={t('accesses.field.category')} htmlFor="access-category">
          <Select
            id="access-category"
            value={category}
            onChange={(event) => setCategory(event.target.value as AccessCategory)}
          >
            {CATEGORY_ORDER.map((value) => (
              <option key={value} value={value}>
                {t(CATEGORY_KEY[value])}
              </option>
            ))}
          </Select>
        </Field>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-2xs tracking-wide text-text-3 uppercase">
            {t('accesses.form.whereItLives')}
          </legend>

          <label
            className={cn(
              'hairline flex items-start gap-2 rounded-[var(--radius-card)] px-2 py-2',
              critical ? 'opacity-50' : 'cursor-pointer hover:bg-surface-2',
            )}
          >
            <input
              type="radio"
              name="access-kind"
              value="stored"
              checked={kind === 'stored'}
              disabled={critical}
              onChange={() => setKind('stored')}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1.5 text-xs text-text-1">
                <Lock size={12} aria-hidden="true" />
                {t('accesses.form.stored.title')}
              </span>
              <span className="text-2xs text-text-3">{t('accesses.form.stored.description')}</span>
            </span>
          </label>

          <label
            className={cn(
              'hairline flex cursor-pointer items-start gap-2 rounded-[var(--radius-card)] px-2 py-2',
              'hover:bg-surface-2',
            )}
          >
            <input
              type="radio"
              name="access-kind"
              value="vaulted"
              checked={kind === 'vaulted'}
              onChange={() => setKind('vaulted')}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1.5 text-xs text-text-1">
                <ShieldAlert size={12} aria-hidden="true" />
                {t('accesses.form.vaulted.title')}
              </span>
              <span className="text-2xs text-text-3">{t('accesses.form.vaulted.description')}</span>
            </span>
          </label>

          {critical ? (
            <p role="status" className="text-2xs text-text-2">
              {t('accesses.form.criticalExplanation')}
            </p>
          ) : null}
        </fieldset>

        <Field label={t('accesses.field.url')} htmlFor="access-url">
          <Input
            id="access-url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://admin.ejemplo.com"
          />
        </Field>

        <Field label={t('accesses.field.username')} htmlFor="access-username">
          <Input
            id="access-username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="off"
          />
        </Field>

        {kind === 'stored' ? (
          <Field
            label={t('accesses.field.secret')}
            htmlFor="access-secret"
            hint={t('accesses.form.secretHint')}
          >
            <Input
              id="access-secret"
              type="password"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              autoComplete="new-password"
            />
          </Field>
        ) : (
          <>
            <Field label={t('accesses.field.vaultProvider')} htmlFor="access-vault-provider">
              <Input
                id="access-vault-provider"
                value={vaultProvider}
                onChange={(event) => setVaultProvider(event.target.value)}
              />
            </Field>
            <Field
              label={t('accesses.field.vaultItemUrl')}
              htmlFor="access-vault-url"
              hint={t('accesses.form.vaultItemUrlHint')}
            >
              <Input
                id="access-vault-url"
                value={vaultItemUrl}
                onChange={(event) => setVaultItemUrl(event.target.value)}
                placeholder="https://start.1password.com/open/i?a=…"
              />
            </Field>
          </>
        )}

        <Field label={t('common.label.notes')} htmlFor="access-notes">
          <Textarea
            id="access-notes"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </form>
    </Sheet>
  )
}
